import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Fab,
  IconButton,
  TextField,
  Typography
} from "@material-ui/core";
import ChatBubbleOutlineIcon from "@material-ui/icons/ChatBubbleOutline";
import CloseIcon from "@material-ui/icons/Close";
import { useMutation } from "@apollo/client";
import React, { useEffect, useState } from "react";
import PhoneInput from "react-phone-number-input";
import { useLocation } from "react-router-dom";
import { useSelector } from "react-redux";

import { IAppState } from "../../store";
import { SUBMIT_BOOKING_ENQUIRY } from "../../graphql/bookings/submitBookingEnquiryMutation";
import { formatGraphQLErrorMessage } from "../utils";
import { useSnackBar } from "../SnackBarContext/SnackBarContext";
import { SnackBarVariant } from "../SnackbarWrapper/SnackbarWrapper";
import { useBookingEnquiryContext } from "./BookingEnquiryContext";
import styles from "./BookingEnquiryWidget.module.css";
import { getDefaultCountryCode } from "../../utils/localized.syntex";
import { getOrCreateFunnelSessionId } from "../../utils/funnelTracking";

const BOOKING_JOURNEY_PATHS = ["/new-booking", "/update-booking", "/quote", "/booking", "/fleet-allocation"];

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

interface IFormState {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  message: string;
}

const EMPTY_FORM: IFormState = {
  firstName: "",
  lastName: "",
  email: "",
  phone: "",
  message: ""
};

export function BookingEnquiryWidget() {
  const location = useLocation();
  const website = useSelector((state: IAppState) => state.consumerWebsiteReducer.consumerWebsite);
  const user = useSelector((state: IAppState) => state.authReducer.user);
  const snackbar = useSnackBar();
  const { snapshot } = useBookingEnquiryContext();

  const [open, setOpen] = useState<boolean>(false);
  const [form, setForm] = useState<IFormState>(EMPTY_FORM);
  const [errorMessage, setErrorMessage] = useState<string>("");

  const isLoggedIn = Boolean(user && user.id);

  useEffect(() => {
    if (open) {
      setErrorMessage("");
      if (isLoggedIn && user) {
        setForm({
          firstName: user.firstName || "",
          lastName: user.lastName || "",
          email: user.email || "",
          phone: user.phoneNumber?.phone || "",
          message: ""
        });
      } else {
        setForm(EMPTY_FORM);
      }
    }
  }, [open, isLoggedIn, user]);

  const [submitBookingEnquiry, { loading }] = useMutation(SUBMIT_BOOKING_ENQUIRY, {
    onCompleted: () => {
      snackbar({
        message: "Thanks! Our team will get in touch with you shortly.",
        variant: SnackBarVariant.SUCCESS
      });
      setOpen(false);
    },
    onError: (error) => {
      snackbar({
        message: formatGraphQLErrorMessage(error.message),
        variant: SnackBarVariant.ERROR
      });
    }
  });

  if (!website?.enableBookingEnquiry) {
    return null;
  }
  if (!BOOKING_JOURNEY_PATHS.some((path) => location.pathname.startsWith(path))) {
    return null;
  }

  const handleSubmit = () => {
    if (!snapshot.branchId) {
      const isSubscriptionStage = snapshot.stage?.startsWith("SUBSCRIPTION_");
      setErrorMessage(isSubscriptionStage
        ? "Please select a vehicle before sending an enquiry."
        : "Please select a pickup location before sending an enquiry.");
      return;
    }
    if (!form.firstName.trim() || !form.lastName.trim()) {
      setErrorMessage("Please enter your first and last name.");
      return;
    }
    if (!EMAIL_REGEX.test(form.email)) {
      setErrorMessage("Please enter a valid email address.");
      return;
    }
    if (!form.phone) {
      setErrorMessage("Please enter a phone number.");
      return;
    }
    setErrorMessage("");
    submitBookingEnquiry({
      variables: {
        branchId: snapshot.branchId,
        enquiry: {
          firstName: form.firstName.trim(),
          lastName: form.lastName.trim(),
          email: form.email.trim(),
          phoneNumber: form.phone,
          message: form.message.trim() || undefined,
          stage: snapshot.stage,
          vehicleGroupId: snapshot.vehicleGroupId,
          vehicleGroupName: snapshot.vehicleGroupName,
          pickupDateTime: snapshot.pickupDateTime,
          dropoffDateTime: snapshot.dropoffDateTime,
          rentalPrice: snapshot.rentalPrice,
          insuranceName: snapshot.insuranceName,
          insurancePrice: snapshot.insurancePrice,
          addonsPrice: snapshot.addonsPrice,
          estimatedTotal: snapshot.estimatedTotal,
          bookingReferenceNumber: snapshot.bookingReferenceNumber,
          addons: snapshot.addons,
          // NU-12910: a visitor who never reached Personal Details but did
          // use "Need Help" should still land in the identified/contactable
          // lead list, not the anonymous drop-off count.
          ...(website?.enableUserDropoffTrackingWebsite && { funnelSessionId: getOrCreateFunnelSessionId() })
        }
      }
    });
  };

  return (
    <>
      <Fab
        variant="extended"
        size="medium"
        onClick={() => setOpen(true)}
        className={styles.fab}
      >
        <ChatBubbleOutlineIcon style={{ marginRight: 8 }} />
        Need Help?
      </Fab>
      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <Typography variant="h6">Talk to us about this booking</Typography>
            <IconButton size="small" onClick={() => setOpen(false)}>
              <CloseIcon />
            </IconButton>
          </div>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" style={{ marginBottom: 16, color: "#7f8c8d" }}>
            Have a question, or want to negotiate the price before you complete your booking? Send us your details and we'll get in touch.
          </Typography>
          <div style={{ display: "flex", gap: 12, marginBottom: 12 }}>
            <TextField
              label="First name"
              fullWidth
              value={form.firstName}
              disabled={isLoggedIn}
              onChange={(e) => setForm({ ...form, firstName: e.target.value })}
            />
            <TextField
              label="Last name"
              fullWidth
              value={form.lastName}
              disabled={isLoggedIn}
              onChange={(e) => setForm({ ...form, lastName: e.target.value })}
            />
          </div>
          <TextField
            label="Email"
            fullWidth
            style={{ marginBottom: 12 }}
            value={form.email}
            disabled={isLoggedIn}
            onChange={(e) => setForm({ ...form, email: e.target.value })}
          />
          <div className={styles.phoneWrap} style={{ marginBottom: 12 }}>
            <PhoneInput
              international={false}
              defaultCountry={getDefaultCountryCode(website.organisation.address.country)}
              placeholder="Phone number"
              value={form.phone}
              disabled={isLoggedIn && Boolean(user?.phoneNumber?.phone)}
              onChange={(value) => setForm({ ...form, phone: value || "" })}
            />
          </div>
          <TextField
            label="Your message (optional)"
            fullWidth
            multiline
            minRows={3}
            value={form.message}
            onChange={(e) => setForm({ ...form, message: e.target.value })}
          />
          {errorMessage && (
            <Typography variant="body2" style={{ color: "#c62828", marginTop: 12 }}>
              {errorMessage}
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>Cancel</Button>
          <Button variant="contained" color="primary" disabled={loading} onClick={handleSubmit}>
            Send Enquiry
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
